import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Modal, Select, Spin, Tag } from "antd";
import { SwapRightOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { setMoveToCompany, setMoveToSite } from "../../../features/chamber.store";
import { getChamberMoveInfo } from "../../../features/chamber.store";
import { NonFormikFormField } from "./FormField";
import FormSection from "./FormSection";
import { GET } from "../../../service/base-api-service";
import "./styles/move-chamber-modal.scss";

export const MoveChamberModal = ({ show, onCancel, onSubmit }) => {
  const chamberMoveInfo = useSelector(getChamberMoveInfo);
  const dispatch = useDispatch();

  const [companyOptions, setCompanyOptions] = useState([]);
  const [siteOptions, setSiteOptions] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingSites, setLoadingSites] = useState(false);

  const selectedCompany = chamberMoveInfo.toCompany;
  const selectedSite = chamberMoveInfo.toSite;

  useEffect(() => {
    if (!show) return;

    const loadCompanies = async () => {
      setLoadingCompanies(true);
      try {
        const payload = await GET("v1/admin/tenant/minimal");
        const options = (Array.isArray(payload) ? payload : [])
          .map((item) => ({
            value: item.identifier,
            label: item.name || item.identifier,
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));

        setCompanyOptions(options);
      } catch (err) {
        console.error("MOVE MODAL: failed to load companies", err);
        setCompanyOptions([]);
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadCompanies();
  }, [show]);

  useEffect(() => {
    if (!show || !selectedCompany) {
      setSiteOptions([]);
      return;
    }

    const loadSites = async () => {
      setLoadingSites(true);
      try {
        const payload = await GET("v1/admin/site/minimal", {
          params: { tenantId: selectedCompany },
        });

        const options = (Array.isArray(payload) ? payload : [])
          .map((item) => ({
            value: item.id,
            label: item.name || String(item.id),
          }))
          .sort((a, b) => String(a.label).localeCompare(String(b.label)));

        setSiteOptions(options);
      } catch (err) {
        console.error("MOVE MODAL: failed to load sites", err);
        setSiteOptions([]);
      } finally {
        setLoadingSites(false);
      }
    };

    loadSites();
  }, [show, selectedCompany]);

  const canSubmit = useMemo(
    () => Boolean(selectedCompany && selectedSite),
    [selectedCompany, selectedSite],
  );

  return (
    <Modal
      className="move-chamber-modal"
      width={860}
      title={(
        <div className="move-modal-title-wrap">
          <div className="move-modal-title">Move Chamber</div>
          <div className="move-modal-subtitle">Transfer destination for <strong>{chamberMoveInfo.selectedChamber.chamberName}</strong></div>
        </div>
      )}
      cancelButtonProps={{ style: { display: "none" } }}
      visible={show}
      onCancel={onCancel}
      onOk={onSubmit}
      okText="Move"
      okButtonProps={{ disabled: !canSubmit }}
    >
      <FormSection className="move-chamber-form-section">
        <div className="move-modal-badge-row">
          <Tag color="green">Current Location</Tag>
          <SwapRightOutlined className="swap-icon" />
          <Tag color="blue">New Location</Tag>
        </div>

        <div className="move-modal-grid">
          <div className="move-col from-col">
            <div className="panel-title">From</div>
            <div className="panel-wrap panel-from">
              <NonFormikFormField
                disabled
                name="fromTenant"
                labelText="Company"
                value={chamberMoveInfo.fromCompany.tenantName}
              />
              <NonFormikFormField
                disabled
                name="fromSite"
                labelText="Site"
                value={chamberMoveInfo.fromSite.siteName}
              />
            </div>
          </div>

          <div className="move-arrow-wrap" aria-hidden="true">
            <ArrowRightOutlined />
          </div>

          <div className="move-col to-col">
            <div className="panel-title">To</div>
            <div className="panel-wrap panel-to">
              <div className="select-field-block">
                <div className="field-label">Company</div>
                <Spin spinning={loadingCompanies}>
                  <Select
                    showSearch
                    allowClear
                    value={selectedCompany || undefined}
                    placeholder="Select Company"
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                    options={companyOptions}
                    onChange={(val) => {
                      dispatch(setMoveToCompany(val || ""));
                      dispatch(setMoveToSite(""));
                    }}
                  />
                </Spin>
              </div>

              <div className="select-field-block">
                <div className="field-label">Site</div>
                <Spin spinning={loadingSites}>
                  <Select
                    showSearch
                    allowClear
                    value={selectedSite || undefined}
                    placeholder="Select Site"
                    style={{ width: "100%" }}
                    optionFilterProp="label"
                    options={siteOptions}
                    onChange={(val) => dispatch(setMoveToSite(val || ""))}
                    disabled={!selectedCompany}
                  />
                </Spin>
              </div>
            </div>
          </div>
        </div>
      </FormSection>
    </Modal>
  );
};